import {
  Alert,
  Badge,
  Code,
  Group,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { graphql, type ResultOf } from "gql.tada";
import { useQuery } from "@apollo/client/react";
import { IconBulb } from "@tabler/icons-react";

// ============================================================================
// ValidationStructuredErrorList
// ============================================================================
//
// Surfaces the per-error rows produced by the rule-set validator, including
// the human-readable message, the preset-author remediation hint, the
// offender context (rule / team / game / group / score-row), and — when the
// error originated from a Lua throw — the raw Lua message and line number.
//
// These rows are distinct from the event-based `ValidationErrorList` which
// streams ERROR-kind events from the event log. This component consumes the
// structured `result.errors` GraphQL surface that is the authoritative
// machine-readable error record for the run.
//
// Backend-authored content (Lua messages, hints, team/rule names) is rendered
// via React children so JSX auto-escaping prevents HTML/XSS injection. Long
// unbroken strings are wrapped with `overflow-wrap: anywhere` so a hostile or
// malformed preset cannot break the page layout by emitting a giant tokenless
// string.
// ============================================================================

const VALIDATION_JOB_STRUCTURED_ERRORS_QUERY = graphql(`
  query ValidationJobStructuredErrors($jobId: ID!) {
    stepRuleSetValidationJob(jobId: $jobId) {
      id
      status
      result {
        errors {
          id
          code
          message
          hint
          invariantCode
          luaError {
            scriptContextType
            line
            column
            message
          }
          offender {
            __typename
            ... on RuleOffenderModel {
              kind
              ruleName
              trigger
            }
            ... on CrossStepRuleOffenderModel {
              kind
              dataSource
              usage
            }
            ... on GameOffenderModel {
              kind
              groupIndex
              roundIndex
              gameIndex
              status
            }
            ... on RoundOffenderModel {
              kind
              groupIndex
              roundIndex
            }
            ... on GroupOffenderModel {
              kind
              groupIndex
              groupName
            }
            ... on TeamOffenderModel {
              kind
              teamName
              currentStatus
            }
            ... on ScoreRowOffenderModel {
              kind
              scope
              score
              rank
              status
            }
          }
        }
      }
    }
  }
`);

type StructuredErrorRow = NonNullable<
  NonNullable<
    ResultOf<
      typeof VALIDATION_JOB_STRUCTURED_ERRORS_QUERY
    >["stepRuleSetValidationJob"]
  >["result"]
>["errors"][number];

type Offender = NonNullable<StructuredErrorRow["offender"]>;

/**
 * CSS that neutralises potentially adversarial message payloads (Lua throws,
 * hint text) so they cannot break the page layout. Long tokenless strings
 * wrap, and `<Code>` blocks keep their monospace presentation while still
 * wrapping long lines.
 */
const BREAK_ANYWHERE_STYLE = {
  overflowWrap: "anywhere",
  wordBreak: "break-word",
} as const;

const CODE_WRAP_STYLE = {
  ...BREAK_ANYWHERE_STYLE,
  whiteSpace: "pre-wrap",
} as const;

interface ValidationStructuredErrorListProps {
  readonly jobId: string;
}

function ValidationStructuredErrorList(
  props: ValidationStructuredErrorListProps,
) {
  const { data, loading, error } = useQuery(
    VALIDATION_JOB_STRUCTURED_ERRORS_QUERY,
    { variables: { jobId: props.jobId } },
  );

  if (loading && !data) {
    return (
      <Stack gap="sm">
        <Skeleton height={16} width={200} />
        <Skeleton height={80} radius="sm" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Text size="xs" c="red">
        Failed to load structured validation errors.
      </Text>
    );
  }

  const errors: readonly StructuredErrorRow[] =
    data?.stepRuleSetValidationJob?.result?.errors ?? [];
  if (errors.length === 0) return null;

  return (
    <Stack gap="sm">
      <Text fw={600} c="red">
        Structured errors ({errors.length})
      </Text>
      {errors.map((row) => (
        <Alert
          key={row.id}
          color="red"
          title={
            <Group gap="xs" wrap="wrap" align="flex-start">
              <Badge color="red" variant="light">
                {row.invariantCode ?? row.code}
              </Badge>
              <Text size="sm" fw={500} style={BREAK_ANYWHERE_STYLE}>
                {row.message}
              </Text>
            </Group>
          }
        >
          <Stack gap="xs">
            {row.luaError ? (
              <Text size="xs" c="dimmed" style={BREAK_ANYWHERE_STYLE}>
                Lua [{row.luaError.scriptContextType}] line{" "}
                {row.luaError.line}:{" "}
                <Code style={CODE_WRAP_STYLE}>{row.luaError.message}</Code>
              </Text>
            ) : null}
            {row.offender ? (
              <Text size="xs" c="dimmed" style={BREAK_ANYWHERE_STYLE}>
                Offender: {formatOffender(row.offender)}
              </Text>
            ) : null}
            {row.hint ? (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconBulb size={14} />
                <Text size="xs" style={BREAK_ANYWHERE_STYLE}>
                  {row.hint}
                </Text>
              </Group>
            ) : null}
          </Stack>
        </Alert>
      ))}
    </Stack>
  );
}

/**
 * Format an offender union value into a short human-readable snippet.
 * Mirrors the backend's `formatOffender` helper so frontend and server
 * messaging stay aligned. Narrows on Apollo's `__typename` which gql.tada
 * types as a string-literal per union member — the schema-level `kind` enum
 * is typed as the full `OffenderKind` union on every member and cannot
 * narrow the discriminated union at the TypeScript level.
 */
function formatOffender(offender: Offender): string {
  switch (offender.__typename) {
    case "RuleOffenderModel":
      return `rule "${offender.ruleName}" (trigger=${offender.trigger})`;
    case "CrossStepRuleOffenderModel":
      return `cross-step rule (source=${offender.dataSource}, usage=${offender.usage})`;
    case "GameOffenderModel": {
      const status = ` [${offender.status}]`;
      return (
        `group ${offender.groupIndex}, round ${offender.roundIndex}, game ${offender.gameIndex}` +
        status
      );
    }
    case "RoundOffenderModel":
      return `group ${offender.groupIndex}, round ${offender.roundIndex}`;
    case "GroupOffenderModel":
      return `group ${offender.groupIndex} (${offender.groupName})`;
    case "TeamOffenderModel": {
      const status = offender.currentStatus
        ? ` [${offender.currentStatus}]`
        : "";
      return `team "${offender.teamName}"${status}`;
    }
    case "ScoreRowOffenderModel": {
      const score =
        typeof offender.score === "number" ? ` score=${offender.score}` : "";
      const rank =
        typeof offender.rank === "number" ? ` rank=${offender.rank}` : "";
      const status = offender.status ? ` status=${offender.status}` : "";
      return `${offender.scope}-scoped score row${score}${rank}${status}`;
    }
    default:
      return "unknown offender";
  }
}

export {
  ValidationStructuredErrorList,
  VALIDATION_JOB_STRUCTURED_ERRORS_QUERY,
};
export type { ValidationStructuredErrorListProps };
